
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient from '../utils/apiClient';
import { FaUser, FaLock, FaEnvelope, FaArrowLeft } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc'; // Add Google icon
import config from '../config';

const Signup = ({ onBackToLogin, isDarkMode }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  // NEW: modal control for verification message
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  useEffect(() => {
    // Check for OAuth callback tokens in URL
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token');
    const refreshToken = urlParams.get('refresh_token');

    if (accessToken) {
      // OAuth signup/login success - clean URL and go back to login to handle it
      const newUrl = window.location.pathname;
      window.history.replaceState(null, '', newUrl);
      onBackToLogin(); // This will trigger the login component which will handle the tokens
    }
  }, [onBackToLogin]);

  const extractErrorMessage = (err) => {
    const detail = err?.response?.data?.detail ?? err?.message;
    if (!detail) return 'Failed to register. Please try again with different credentials.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) => {
          const loc = Array.isArray(d?.loc) ? d.loc.join('.') : d?.loc;
          const msg = d?.msg || d?.message || JSON.stringify(d);
          return loc ? `${loc}: ${msg}` : msg;
        })
        .filter(Boolean);
      return msgs.join('\n') || 'Validation error';
    }
    try { return JSON.stringify(detail); } catch { return String(detail); }
  };

  const validateForm = () => {
    setError(null);
    if (!firstName || !lastName || !username || !email || !password || !confirmPassword) {
      setError('All fields are required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setError('Please enter a valid email address'); return false; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return false; }
    if (password.length < 6) { setError('Password must be at least 6 characters long'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await apiClient.post(`${config.API_BASE_URL}/auth/register`, {
        email,
        username,
        password,
        confirm_password: confirmPassword,
        first_name: firstName,
        last_name: lastName,
      });

      // Clear inputs and show success + modal
      setFirstName('');
      setLastName('');
      setUsername('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');

      setSuccess(true);
      setShowVerifyModal(true); // open the pop-up

      // Removed: setTimeout(() => onBackToLogin(), 2000)
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // Redirect to backend Google OAuth endpoint (same as login - backend handles both)
    window.location.href = `${config.API_BASE_URL}/auth/google/login`;
  };

  return (
    <div className="flex justify-center items-center min-h-screen px-4">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className={`w-full max-w-md p-8 rounded-2xl border ${
          isDarkMode
            ? 'bg-gray-900/90 border-gray-700 shadow-[0_8px_30px_rgb(0,0,0,0.35)] backdrop-blur'
            : 'bg-white/95 border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.08)] backdrop-blur'
        }`}
      >
        {/* Header with Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src={require('../assets/logo.png')}
              alt="JobxBharat Logo"
              className="h-12 w-12 object-contain"
            />
            <h1 className="text-3xl font-extrabold">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-600">Jobx</span><span className="text-brand-saffron">Bharat</span>
            </h1>
          </div>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>
            Create an account to start your job search journey
          </p>
        </div>

        {/* Success message */}
        {success ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`${isDarkMode ? 'bg-green-900/30 text-green-200' : 'bg-green-100 text-green-700'} p-4 rounded-lg text-center mb-4`}
          >
            Registration successful! Please check your email for a verification link.
          </motion.div>
        ) : (
          <>
            {/* Google Sign Up Button */}
            <motion.button
              type="button"
              onClick={handleGoogleSignup}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full mb-6 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-3 transition-all duration-200 border ${
                isDarkMode
                  ? 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'
                  : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <FcGoogle className="h-5 w-5" />
              Sign up with Google
            </motion.button>

            {/* Divider */}
            <div className="relative mb-6">
              <div className={`absolute inset-0 flex items-center`}>
                <div className={`w-full border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-2 ${isDarkMode ? 'bg-gray-900 text-gray-400' : 'bg-white text-gray-500'}`}>
                  Or continue with email
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* First Name */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your first name"
                    required
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                        : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                    }`}
                  />
                </div>
              </div>

              {/* Last Name */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Last Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter your last name"
                    required
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                        : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                    }`}
                  />
                </div>
              </div>

              {/* Username */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Choose a username"
                    required
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                        : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                    }`}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                        : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                    }`}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    required
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                        : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                    }`}
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-600 text-white focus:ring-gray-400 focus:border-gray-400'
                        : 'bg-gray-50 border-gray-300 text-gray-900 focus:ring-black focus:border-black'
                    }`}
                  />
                </div>
              </div>

              {/* Error */}
              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`${isDarkMode ? 'bg-red-900/30 text-red-200' : 'bg-red-100 text-red-700'} p-3 rounded-lg`}
                >
                  {error}
                </motion.div>
              )}

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
                className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl'}`}
              >
                {loading ? 'Creating Account…' : 'Sign Up'}
              </motion.button>

              {/* Back to Login */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={onBackToLogin}
                  className={`flex items-center justify-center mx-auto text-sm ${
                    isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-black'
                  }`}
                >
                  <FaArrowLeft className="mr-2" /> Back to Login
                </button>
              </div>
            </form>
          </>
        )}
      </motion.div>

      {/* Modal: Verification Info */}
      {showVerifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className={`w-full max-w-md mx-4 rounded-xl p-6 ${
              isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'
            }`}
          >
            <h3 className="text-xl font-semibold mb-2">Registration successful</h3>
            <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
              A verification link has been sent to your email. Please open your email and click the link to verify your account. You will not be able to sign in until verification is complete.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setShowVerifyModal(false)}
                className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-200 text-gray-800'}`}
              >
                Close
              </button>
              <button
                onClick={() => {
                  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                  if (isMobile) {
                    // Try to open Gmail app on mobile, fallback to web
                    window.location.href = 'googlegmail://';
                    // Fallback to web after a short delay if app doesn't open
                    setTimeout(() => {
                      window.open('https://mail.google.com', '_blank');
                    }, 500);
                  } else {
                    window.open('https://mail.google.com', '_blank');
                  }
                }}
                className="px-4 py-2 rounded bg-gradient-to-r from-red-500 to-red-600 text-white font-medium hover:from-red-600 hover:to-red-700 transition-all flex items-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                </svg>
                Open Gmail
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Signup;